import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, Shield, User, Search, Filter, ChevronLeft, ChevronRight,
  Ban, CheckCircle, AlertTriangle, Lock, Unlock, Leaf
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  ATTIVO: "bg-green-500/20 text-green-400 border-green-500/30",
  SOSPESO: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  BLOCCATO: "bg-red-500/20 text-red-400 border-red-500/30"
};

const statusIcons = {
  ATTIVO: CheckCircle,
  SOSPESO: AlertTriangle,
  BLOCCATO: Lock
};

const roleColors = {
  ADMIN: "bg-purple-500/20 text-purple-400",
  STAFF: "bg-blue-500/20 text-blue-400",
  OFFICINA: "bg-emerald-500/20 text-emerald-400",
  RICAMBISTA: "bg-amber-500/20 text-amber-400",
  user: "bg-zinc-500/20 text-zinc-400"
};

export default function AdminUsersNew({ user }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (search) params.append("search", search);
      if (roleFilter !== "ALL") params.append("role", roleFilter);
      if (statusFilter !== "ALL") params.append("status", statusFilter);

      const response = await authFetch(`${API}/admin/users/list?${params}`, { 
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      }
    } catch (error) {
      toast.error("Errore nel caricamento utenti");
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, sortBy, sortOrder, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const StatusIcon = ({ status }) => {
    const Icon = statusIcons[status] || User;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div data-testid="admin-users-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Gestione Utenti</h1>
        <div className="text-zinc-500">{total} utenti registrati</div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Cerca per nome, email, telefono..."
              value={search}
              onChange={handleSearchChange}
              className="pl-10 bg-zinc-950 border-zinc-800"
            />
          </div>
        </div>
        
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-zinc-950 border-zinc-800">
            <SelectValue placeholder="Ruolo" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="ALL">Tutti i ruoli</SelectItem>
            <SelectItem value="ADMIN">Admin</SelectItem>
            <SelectItem value="STAFF">Staff</SelectItem>
            <SelectItem value="OFFICINA">Officina</SelectItem>
            <SelectItem value="RICAMBISTA">Ricambista</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-zinc-950 border-zinc-800">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="ALL">Tutti gli stati</SelectItem>
            <SelectItem value="ATTIVO">Attivo</SelectItem>
            <SelectItem value="SOSPESO">Sospeso</SelectItem>
            <SelectItem value="BLOCCATO">Bloccato</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-40 bg-zinc-950 border-zinc-800">
            <SelectValue placeholder="Ordina per" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="created_at">Data creazione</SelectItem>
            <SelectItem value="last_login_at">Ultimo accesso</SelectItem>
            <SelectItem value="points">Punti</SelectItem>
            <SelectItem value="name">Nome</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
          className="text-zinc-400"
        >
          {sortOrder === "desc" ? "↓" : "↑"}
        </Button>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Utente</th>
                <th>Contatti</th>
                <th>Ruolo</th>
                <th>Stato</th>
                <th>RENTRI</th>
                <th className="text-right">Punti</th>
                <th>Data Registrazione</th>
                <th>Ultimo Accesso</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr 
                  key={u.user_id} 
                  className="cursor-pointer hover:bg-zinc-800/50"
                  onClick={() => navigate(`/admin/users/${u.user_id}`)}
                >
                  <td>
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-sm" />
                      ) : (
                        <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                          <User className="w-5 h-5 text-zinc-600" />
                        </div>
                      )}
                      <div>
                        <span className="text-white font-medium">{u.name || u.username}</span>
                        {u.company_name && (
                          <div className="text-xs text-zinc-500">{u.company_name}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="text-sm">
                      <div className="text-zinc-400">{u.email}</div>
                      {u.phone && <div className="text-zinc-500">{u.phone}</div>}
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs uppercase font-medium ${roleColors[u.role] || roleColors.user}`}>
                      {u.role || "user"}
                    </span>
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${statusColors[u.status] || statusColors.ATTIVO}`}>
                      <StatusIcon status={u.status || "ATTIVO"} />
                      {u.status || "ATTIVO"}
                    </span>
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      u.rentri_enabled 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-zinc-800 text-zinc-500'
                    }`}>
                      <Leaf className="w-3 h-3" />
                      {u.rentri_enabled ? 'Attivo' : 'Off'}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="font-['JetBrains_Mono'] text-primary font-medium">
                      {u.points || 0}
                    </span>
                  </td>
                  <td className="font-['JetBrains_Mono'] text-zinc-500 text-sm">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "-"}
                  </td>
                  <td className="font-['JetBrains_Mono'] text-zinc-500 text-sm">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("it-IT") : "-"}
                  </td>
                  <td>
                    <ChevronRight className="w-4 h-4 text-zinc-600" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
          <div className="text-sm text-zinc-500">
            Pagina {page} di {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
